import { AddressSuggestionFilterInput, AddressSuggestionResponse } from '@@types'
import { gql, useLazyQuery, useQuery } from '@apollo/client'

// region Query Types
interface SearchForAddressSuggestionsQuery {
  searchForAddressSuggestions: AddressSuggestionResponse
}

interface SearchForAddressSuggestionsVariables {
  filter: AddressSuggestionFilterInput
}
// endregion

// region GraphQL Query
export const GQL_QUERY_SEARCH_FOR_ADDRESS_SUGGESTIONS = gql`
  query searchForAddressSuggestions($filter: AddressSuggestionFilterInput!) {
    searchForAddressSuggestions(addressFilter: $filter) {
      suggestions {
        value
        unrestricted_value
        data {
          city
          house
          house_type
          block
          block_type
          street_with_type
          settlement
          latitude: geo_lat
          longitude: geo_lon
          qc_geo
        }
      }
    }
  }
`
// endregion

/**
 * Validates that filter has either coordinates or address
 */
export const validateFilterInputVariable = (filter: AddressSuggestionFilterInput) =>
  (filter.latitude && filter.longitude) || filter.address

/**
 * Hook for searching address suggestions by coordinates or address text
 * Used for reverse geocoding (coordinates → address) and forward geocoding (text → suggestions)
 */
export const useSearchForAddressSuggestions = (
  filter: AddressSuggestionFilterInput = {},
  skip = false
) => {
  const { data, loading, error, refetch } = useQuery<
    SearchForAddressSuggestionsQuery,
    SearchForAddressSuggestionsVariables
  >(GQL_QUERY_SEARCH_FOR_ADDRESS_SUGGESTIONS, {
    variables: { filter },
    skip: skip || !validateFilterInputVariable(filter),
    fetchPolicy: 'network-only',
  })

  const suggestions = data?.searchForAddressSuggestions?.suggestions ?? []
  const firstSuggestion = suggestions[0] ?? null

  return {
    suggestions,
    firstSuggestion,
    data,
    loading,
    error,
    refetch,
  }
}

/**
 * Lazy hook for programmatically searching address suggestions
 * Useful for reverse geocoding on map click/drag
 */
export const useLazySearchForAddressSuggestions = () => {
  const [ search, { data, loading, error } ] = useLazyQuery<
    SearchForAddressSuggestionsQuery,
    SearchForAddressSuggestionsVariables
  >(GQL_QUERY_SEARCH_FOR_ADDRESS_SUGGESTIONS, {
    fetchPolicy: 'network-only',
  })

  const suggestions = data?.searchForAddressSuggestions?.suggestions ?? []
  const firstSuggestion = suggestions[0] ?? null

  return [
    search,
    {
      suggestions,
      firstSuggestion,
      data,
      loading,
      error,
    },
  ] as const
}
